import { useState } from 'react'
import Add from '@mui/icons-material/Add'
import RuleOutlined from '@mui/icons-material/RuleOutlined'
import { Button, CircularProgress, Stack, Typography } from '@mui/material'
import { useGetApprovalPolicies } from '@/service-library/hooks/approval-policies'
import { useProjectContext } from '../project-tables/ProjectProvider'
import ApprovalPolicyCard from './ApprovalPolicyCard'
import ApprovalPolicyEditor from './ApprovalPolicyEditor'

// 'new' opens a fresh editor at the top of the list; a policy id opens that
// policy's editor in place of its card. Only one of either at a time.
type EditingState = string | 'new' | null

function EmptyState() {
  return (
    <Stack
      alignItems="center"
      spacing={0.5}
      sx={{ textAlign: 'center', px: 2, py: 6 }}
    >
      <RuleOutlined sx={{ fontSize: 32, color: 'text.disabled' }} />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        No approval policies yet
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Create one to require sign-off on matching documents.
      </Typography>
    </Stack>
  )
}

export default function ApprovalPolicyList() {
  const { project } = useProjectContext()
  const [editing, setEditing] = useState<EditingState>(null)

  const {
    approvalPolicies = [],
    isPending,
    queryKey,
  } = useGetApprovalPolicies({
    filters: { limit: '1000', project_id: project.id },
  })

  if (isPending) {
    return (
      <Stack alignItems="center" sx={{ p: 4 }}>
        <CircularProgress />
      </Stack>
    )
  }

  return (
    <Stack spacing={3} sx={{ pb: 6 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
      >
        <Stack spacing={0.5}>
          <Typography variant="h6">Approval Policies</Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 560 }}
          >
            Decide when a document requires sign-off before it can continue
            through the workflow. Each policy describes its conditions in plain
            language and is evaluated against every document in this project.
          </Typography>
        </Stack>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setEditing('new')}
          sx={{ flexShrink: 0 }}
        >
          New Policy
        </Button>
      </Stack>

      <Stack spacing={2}>
        {editing === 'new' && (
          <ApprovalPolicyEditor
            listQueryKey={queryKey}
            onClose={() => setEditing(null)}
          />
        )}

        {approvalPolicies.length === 0 && editing === null && <EmptyState />}

        {approvalPolicies.map((policy) =>
          editing === policy.id ? (
            <ApprovalPolicyEditor
              key={policy.id}
              policy={policy}
              listQueryKey={queryKey}
              onClose={() => setEditing(null)}
            />
          ) : (
            <ApprovalPolicyCard
              key={policy.id}
              policy={policy}
              listQueryKey={queryKey}
              onEdit={() => setEditing(policy.id)}
            />
          ),
        )}
      </Stack>
    </Stack>
  )
}
